import React, {useMemo, useState} from "react";
import {Font} from "../../../../types/Font";
import {useGlobalContext} from "../../../../contexts/globalContext";
import Text from "../../../../components/Text";
import Input from "../../../../components/Input";
import Button from "../../../../components/Button";
import styles from './EditFontModalContent.module.css'
import cx from "classnames";
import {faChevronDown, faChevronRight} from "@fortawesome/free-solid-svg-icons";
import Icon from "../../../../components/Icon";
import ToggleSwitch from "../../../../components/ToggleSwitch";
import {useTranslation} from "react-i18next";
import {useUpdateData} from "../../../../hooks/useUpdateData";
import * as yup from "yup";

type EditFontModalContentProps = {
    font: Font,
    fonts: Font[],
    onSubmit: () => void
}

const EditFontModalContent: React.FC<EditFontModalContentProps> = ({font, fonts, onSubmit}) => {
    const {closeModal, showToast} = useGlobalContext()
    const [showWebsafe, setShowWebsafe] = useState<boolean>(false)
    const { t } = useTranslation(['settings'])

    const updateFontSchema = useMemo(() => {
        return ({
            name: yup
                .string()
                .required()
                .min(3)
                .notOneOf(fonts.filter((f) => f.id !== font.id).map(f => f.name)),
            active: yup.boolean().required()
        })
    },[fonts, font])

    const {
        element,
        changeProp,
        submit,
        isValid,
        error
    } = useUpdateData<Font>('fonts', font, font.id, updateFontSchema)

    const handleSubmit = () => {
        if (error) {
            showToast('top-right', 'error', error)
        } else if (isValid) {
            submit().then((res) => {
                if (res.status === 200) {
                    closeModal()
                    showToast('top-right', 'success', t('fonts.edit_modal.success'))
                } else {
                    showToast('top-right', 'error', t('fonts.edit_modal.error'))
                }
                onSubmit()
            })
        }
    }

    return (
        <div className={cx(styles['modal-wrapper'])}>
            <Input
                type={'text'}
                placeholder={t('fonts.labels.name')}
                property={'name'} value={element.name}
                onChange={(property, value) => changeProp({name: value})}
            />
            <div className={cx(styles['websafe-header'])} onClick={() => setShowWebsafe(prev => !prev)}>
                <Text>{t('fonts.websafe.heading')}</Text>
                <Icon icon={showWebsafe ? faChevronDown : faChevronRight} color={'black'}  size={'sm'}/>
            </div>
            <div className={cx(styles['websafe-wrapper'])} style={{maxHeight: !showWebsafe ? '0px' : '100vh'}}>
                <Text>{t('fonts.websafe.content')}</Text>
            </div>
            <div className={'my-4'}>
                <ToggleSwitch
                    classes={'text-nowrap'}
                    property={'active'}
                    value={element.active} label={t('fonts.labels.active')}
                    onChange={(property, value) => changeProp({active: value})}
                />
            </div>
            <Button
                hierarchy={'primary'}
                classes={'w-100'}
                text={t('fonts.edit_modal.action')}
                fn={() => handleSubmit()}
                disabled={!isValid}
            />
        </div>
    )
}

export default EditFontModalContent
